import { jsx } from 'react/jsx-runtime';
import { memo, forwardRef, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useCancellableCallback } from './use-cancellable-callback.js';

const SAFE_TRIANGLE_TIMEOUT_MS = 300;
const Z_INDEX_DROPDOWN = 'var(--z-index-dropdown)';
/** Companion hook for <SafeTriangle /> polygon */
function useSafeTriangle({ enabled, from, to, debug = false, onTimeout, portalTarget = document.body, }) {
    // Note(drew): these values almost always get updated altogether as one, hence combining.
    // Also worth noting this is the ONLY piece of state in this hook that causes re-renders.
    const [pos, setPos] = useState(() => ({ mouseY: -1, left: 0, top: 0, width: 0, height: 0 }));
    const animationFrame = useRef(0);
    const direction = useRef('right');
    const triangleRef = useRef(null);
    const fromBB = useRef(null);
    const toBB = useRef(null);
    // when this is hidden, refresh all internal state
    useEffect(() => {
        if (!enabled && (fromBB.current || toBB.current)) {
            setPos({ mouseY: -1, left: 0, top: 0, width: 0, height: 0 });
            fromBB.current = null;
            toBB.current = null;
        }
    }, [enabled]);
    const update = useCallback((e) => {
        // don’t recalculate if invisible
        if (!enabled || !from.current || !to.current) {
            return;
        }
        // ignore call if already running
        if (animationFrame.current) {
            return;
        }
        animationFrame.current = requestAnimationFrame(() => {
            // note: at really high update rates, these actually could be unset if the parent is unmounting
            if (!from.current || !to.current) {
                animationFrame.current = null;
                return;
            }
            // refresh bounding boxes ONCE if needed
            fromBB.current ??= from.current.getBoundingClientRect();
            toBB.current ??= to.current.getBoundingClientRect();
            direction.current =
                centerPoint(toBB.current).x < centerPoint(fromBB.current).x ? 'left' : 'right';
            // update mouse coords as long as we’re within the original trigger
            if (from.current?.contains(e.target)) {
                setPos({
                    width: Math.round(direction.current === 'left'
                        ? e.clientX - toBB.current.right
                        : toBB.current.left - e.clientX),
                    height: Math.round(Math.max(fromBB.current.height, toBB.current.height)),
                    left: direction.current === 'left'
                        ? Math.floor(toBB.current.right - 1)
                        : Math.ceil(e.clientX + 1),
                    top: Math.round(toBB.current.top),
                    mouseY: Math.round(e.clientY - toBB.current.top),
                });
            }
            animationFrame.current = null;
        });
    }, [enabled, from, to]);
    const onTimeoutRef = useRef(onTimeout);
    onTimeoutRef.current = onTimeout;
    const props = useCallback(() => ({
        debug,
        direction: direction.current,
        enabled,
        ref: triangleRef,
        boundaryBottom: fromBB.current?.bottom ?? 0,
        boundaryTop: fromBB.current?.top ?? 0,
        onTimeout: onTimeoutRef.current,
        portalTarget,
        ...pos,
    }), [enabled, debug, portalTarget, pos]);
    return useMemo(() => ({ update, props }), [update, props]);
}
/** Get the center of a rect */
function centerPoint(rect) {
    return { x: rect.x + 0.5 * rect.width, y: rect.y + 0.5 * rect.height };
}
const DEFAULT_DEBUG_FILL = '#00ffff80';
const CSS_MOUSE_Y = '--mouse-y';
const TRIANGLE_LEFT = `polygon(0 0, 100% var(${CSS_MOUSE_Y}), 0 100%)`;
const TRIANGLE_RIGHT = `polygon(100% 0, 0 var(${CSS_MOUSE_Y}), 100% 100%)`;
/**
 * Render an SVG polygon emanating from a mouse when navigating submenus or hover tunnels.
 * Note: this is using React.memo() because all props are memoize-able. This should be
 * very performance-conscious, and never re-render unless necessary.
 */
const SafeTriangle = memo(forwardRef(({ boundaryBottom, boundaryTop, debug, direction, enabled, height, left, onTimeout, mouseY, top, onPointerMove, onPointerLeave, width, portalTarget, ...props }, ref) => {
    // Set a timer for 1 second after the user has left the original trigger area
    // (the only way to leave the area is via the top or bottom Y boundary—if a
    // user leaves the left or right X boundary, they are either in the submenu
    // or redrawing the triangle).
    //
    // We want to do this because it’s bad to keep an invisible element
    // on-screen the user doesn’t know about, that’s preventing normal behavior.
    // This follows the assumption that if the user takes > X seconds to move
    // their pointer, they may not be trying to move into the submenu.
    //
    // You can find this exact behavior in macOS.
    const timer = useRef(null);
    const [delayedTimeout, cancelDelayedTimeout] = useCancellableCallback(() => onTimeout?.(), { delay: SAFE_TRIANGLE_TIMEOUT_MS }, [onTimeout]);
    if (!enabled) {
        return null;
    }
    let backgroundColor;
    if (debug) {
        backgroundColor = typeof debug === 'string' ? debug : DEFAULT_DEBUG_FILL;
    }
    return createPortal(jsx("div", { "data-fpl-component": "", ...props, "aria-hidden": true, onPointerMove: (e) => {
            onPointerMove?.(e);
            if (e.pointerType !== 'mouse')
                return;
            if (!onTimeout)
                return;
            const isOutsideBoundary = e.clientY < boundaryTop || e.clientY > boundaryBottom;
            if (isOutsideBoundary && !timer.current) {
                delayedTimeout();
                // Note(drew): look closely—these are NOT logical inverses of one another (hence this is not "else")
            }
            else if (!isOutsideBoundary && timer.current) {
                cancelDelayedTimeout();
            }
        }, onPointerLeave: (e) => {
            onPointerLeave?.(e);
            if (e.pointerType !== 'mouse')
                return;
            cancelDelayedTimeout(); // existing the triangle is seen as a “success” so abort the in-flight onCancel
        }, ref: ref, style: {
            [CSS_MOUSE_Y]: `${Math.round((10_000 * mouseY) / (height || 1)) / 100}%`,
            backgroundColor,
            clipPath: direction === 'left' ? TRIANGLE_LEFT : TRIANGLE_RIGHT,
            height,
            left,
            position: 'fixed',
            top,
            width,
            zIndex: Z_INDEX_DROPDOWN,
        } }), portalTarget);
}));
SafeTriangle.displayName = 'SafeTriangle';

export { SafeTriangle, useSafeTriangle };
//# sourceMappingURL=use-safe-triangle.js.map
